import * as Popover from '@radix-ui/react-popover'
import { Check, ChevronDown, Search } from 'lucide-react'
import { forwardRef, useEffect, useMemo, useState } from 'react'
import { Input } from '../..'
import { cn } from '@shared/lib/utils'

export type SearchableSelectOption = {
  value: string
  label: string
}

interface SearchableSelectProps<TOption extends SearchableSelectOption = SearchableSelectOption> {
  value?: string
  onChange: (value: string) => void
  options: ReadonlyArray<TOption>
  placeholder?: string
  disabled?: boolean
  className?: string
  uppercase?: boolean
  autoFocus?: boolean
  /** Used for filtering and default label rendering. Defaults to `option.label`. */
  getOptionLabel?: (option: TOption) => string
  /** Custom trigger content. Receives `undefined` when no value is selected. */
  renderValue?: (option: TOption | undefined) => React.ReactNode
  /** Custom dropdown row content. Check icon is rendered separately. */
  renderOption?: (option: TOption) => React.ReactNode
}

function formatLabel(label: string, uppercase: boolean) {
  return uppercase ? label.toUpperCase() : label
}

export const SearchableSelect = forwardRef(function SearchableSelect<
  TOption extends SearchableSelectOption = SearchableSelectOption,
>(
  {
    value,
    onChange,
    options,
    placeholder = 'Select option',
    disabled = false,
    className,
    uppercase = true,
    autoFocus,
    getOptionLabel,
    renderValue,
    renderOption,
  }: SearchableSelectProps<TOption>,
  ref: React.ForwardedRef<HTMLButtonElement>
) {
  const [open, setOpen] = useState(false)
  const [query, setQuery] = useState('')

  const resolveLabel = (option: TOption) => getOptionLabel?.(option) ?? option.label
  const selected = options.find((option) => option.value === value)

  const filtered = useMemo(() => {
    if (!query.trim()) return options

    const normalizedQuery = query.toLowerCase()

    return options.filter((option) => resolveLabel(option).toLowerCase().includes(normalizedQuery))
  }, [options, query, getOptionLabel])

  useEffect(() => {
    if (autoFocus && ref && typeof ref !== 'function' && ref.current) {
      ref.current.focus()
    }
  }, [autoFocus, ref])

  const handleOpenChange = (nextOpen: boolean) => {
    setOpen(nextOpen)
    if (!nextOpen) {
      setQuery('')
    }
  }

  const triggerContent = renderValue
    ? renderValue(selected)
    : selected
      ? formatLabel(resolveLabel(selected), uppercase)
      : placeholder

  return (
    <Popover.Root open={open} onOpenChange={handleOpenChange}>
      <Popover.Trigger asChild>
        <button
          ref={ref}
          type="button"
          role="combobox"
          aria-expanded={open}
          aria-haspopup="listbox"
          disabled={disabled}
          className={cn(
            'flex h-[50px] w-full items-center justify-between rounded-md border bg-white px-3 py-1 text-sm',
            'disabled:cursor-not-allowed disabled:opacity-50',
            className
          )}
        >
          <span
            className={cn(
              'min-w-0 flex-1 truncate text-left',
              !selected && !renderValue && 'text-client-neutral-500'
            )}
          >
            {triggerContent}
          </span>

          <ChevronDown className="h-4 w-4 shrink-0 opacity-50" />
        </button>
      </Popover.Trigger>

      <Popover.Portal>
        <Popover.Content
          align="start"
          className="z-50 w-[var(--radix-popover-trigger-width)] rounded-md border bg-white shadow-md"
        >
          <div className="border-b p-2">
            <div className="relative">
              <Search className="absolute left-2 top-1/2 h-4 w-4 -translate-y-1/2 text-client-neutral-500" />

              <Input
                autoFocus
                value={query}
                onChange={(event) => setQuery(event.target.value)}
                placeholder="Search..."
                className="h-9 pl-8"
              />
            </div>
          </div>

          <div className="max-h-72 overflow-y-auto" role="listbox">
            {filtered.map((option) => (
              <button
                key={option.value}
                type="button"
                role="option"
                aria-selected={value === option.value}
                className="flex w-full items-center justify-between gap-2 px-3 py-2 text-left text-sm hover:bg-muted"
                onClick={() => {
                  onChange(option.value)
                  setOpen(false)
                  setQuery('')
                }}
              >
                <span className="min-w-0 flex-1">
                  {renderOption
                    ? renderOption(option)
                    : formatLabel(resolveLabel(option), uppercase)}
                </span>

                {value === option.value && <Check className="h-4 w-4 shrink-0" />}
              </button>
            ))}

            {filtered.length === 0 && (
              <div className="p-4 text-center text-sm text-muted-foreground">No options found</div>
            )}
          </div>
        </Popover.Content>
      </Popover.Portal>
    </Popover.Root>
  )
}) as <TOption extends SearchableSelectOption = SearchableSelectOption>(
  props: SearchableSelectProps<TOption> & { ref?: React.ForwardedRef<HTMLButtonElement> }
) => React.ReactElement
